import { Alert, Box, Divider, Typography } from '@mui/material';
import { FormikErrors, useFormikContext } from 'formik';
import { compact, find, isString } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CommissionIntervalForm,
  FormControls,
  FormikCheckbox,
  FormikNullableBooleanCheckbox,
  FormikNumericField,
  FormikTextField,
  HelpTooltip,
  PaymentTypeAmountLimits,
  TradeMethodTreeSelect,
} from 'components';
import { AppConfigArea, AppConfigKey, P2PProviderType } from 'enums';
import {
  useAppConfigValue,
  useCurrencies,
  useMeta,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TradeMethod } from 'types';

import { ConfigOverridesField } from '../ConfigOverridesField';
import { IgnoreWebhookField } from '../IgnoreWebhookField';
import { P2PProviderCredentials } from '../P2PProviderCredentials';
import { P2PProviderWorkingHours } from '../P2PProviderWorkingHours';

type Props = {
  providerType?: P2PProviderType | string;
  fiatCurrencyId?: string;
  assetCurrencyId?: string;
  onTradeMethodsChange: (event: any) => void;
  isNew?: boolean;
};

type MissedMappingConfig = {
  failOnMissedMapping?: boolean | null;
  failOnMissedMappingPerShopAndProvider?: boolean | null;
};

export const P2PProviderPayinConfig: React.FC<Props> = ({
  providerType,
  fiatCurrencyId,
  isNew,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { getFiatCurrencyCode } = useCurrencies();
  const {
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    isProviderIntervalCommissionsEnabled,
    isProviderWorkingHoursEnabled,
    isProviderLimitsEnabled,
    isP2PProviderIgnoreWebhookAllowedEnabled,
  } = useMeta();

  const { tradeMethods, allTradeMethods, paymentTypes } = useUserContext();
  const missedMappingConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.P2PProvidersCommon,
    key: AppConfigKey.MissedMappingConfig,
  });

  const missedMappingConfig = (missedMappingConfigQueryResult.data
    ?.jsonObjectValue ?? missedMappingConfigQueryResult.data) as
    | MissedMappingConfig
    | undefined;
  const showMissedMappingOverride =
    missedMappingConfig?.failOnMissedMappingPerShopAndProvider === true;
  const missedMappingGlobalDefault =
    (missedMappingConfig?.failOnMissedMapping ?? true) === true;
  const missedMappingGlobalDefaultLabel = t(
    missedMappingGlobalDefault
      ? 'fields.fail_on_missed_mapping_enabled'
      : 'fields.fail_on_missed_mapping_disabled',
  );
  const availableTradeMethods: TradeMethod[] = useMemo(
    () => (isNew ? tradeMethods : allTradeMethods),
    [isNew, tradeMethods, allTradeMethods],
  );

  const fiatCurrenciesIds = useMemo(
    () => compact([fiatCurrencyId]),
    [fiatCurrencyId],
  );

  const { values, errors } = useFormikContext<any>();

  const ownPaymentType = useMemo(() => {
    if (
      !isP2PProviderCanIgnoreOrderPaymentTypeEnabled ||
      !values.payinConfig?.useOwnPaymentType
    ) {
      return null;
    }
    const firstId = values.payinConfig?.tradeMethods?.[0]?.id;
    const tradeMethod = find(allTradeMethods, (tm) => tm.id === firstId);
    return find(paymentTypes, (pt) => pt.id === tradeMethod?.paymentTypeId);
  }, [
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    values.payinConfig?.useOwnPaymentType,
    values.payinConfig?.tradeMethods,
    allTradeMethods,
    paymentTypes,
  ]);

  const multiplePaymentTypesError = useMemo(() => {
    const tradeMethodsErrors = (errors?.payinConfig as FormikErrors<any>)
      ?.tradeMethods;
    return isP2PProviderCanIgnoreOrderPaymentTypeEnabled &&
      values.payinConfig?.useOwnPaymentType &&
      isString(tradeMethodsErrors)
      ? tradeMethodsErrors
      : null;
  }, [
    values.payinConfig?.useOwnPaymentType,
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    errors?.payinConfig,
  ]);

  return (
    <Fragment>
      <Typography variant="h5">{tCommon('common.payin')}</Typography>
      <FormikCheckbox label={t('fields.enabled')} name="payinConfig.enabled" />
      <FormikCheckbox
        label={t('fields.polling_enabled')}
        name="payinConfig.pollingEnabled"
      />
      {isP2PProviderIgnoreWebhookAllowedEnabled && (
        <IgnoreWebhookField fieldPath="payinConfig" />
      )}
      <Box display="flex" alignItems="center">
        <FormikCheckbox
          label={t('fields.cancel_enabled')}
          name="payinConfig.cancelEnabled"
        />
        <HelpTooltip title={t('fields.cancel_enabled_description')} />
      </Box>
      {isP2PProviderCanIgnoreOrderPaymentTypeEnabled && (
        <FormikCheckbox
          name="payinConfig.useOwnPaymentType"
          label={t('fields.use_own_payment_type')}
          tooltip={t('fields.use_own_payment_type_description')}
          {...(ownPaymentType && {
            helperText: t('fields.use_own_payment_type_chosen_value', {
              paymentTypeName: tCommon(`payment_types.${ownPaymentType.code}`, {
                defaultValue: ownPaymentType.name,
              }),
            }),
          })}
        />
      )}
      <FormikTextField
        label={t('fields.url')}
        name="payinConfig.url"
        required={providerType !== P2PProviderType.ManualResolve}
      />

      {showMissedMappingOverride && (
        <Box display="flex" alignItems="center">
          <FormikNullableBooleanCheckbox
            name="payinConfig.failOnMissedMapping"
            label={t('fields.fail_on_missed_mapping')}
            data-testid="p2p-provider-fail-on-missed-mapping-checkbox"
            sx={{ marginRight: 0 }}
          />
          <HelpTooltip
            iconTestId="p2p-provider-fail-on-missed-mapping-tooltip"
            title={t('fields.fail_on_missed_mapping_description', {
              defaultValue: missedMappingGlobalDefaultLabel,
            })}
          />
        </Box>
      )}

      <P2PProviderCredentials
        isPayout={false}
        path="payinConfig.credentials"
        providerType={providerType}
      />

      <FormikNumericField
        label={t('fields.response_timeout')}
        name="payinConfig.responseTimeout"
        suffix={tCommon('suffixes.ms')}
        allowNegative={false}
        fullWidth
        defaultValue={0}
        helperText={t('fields.response_timeout_description')}
      />
      <FormikNumericField
        label={t('fields.expected_fee')}
        name="payinConfig.expectedFee"
        allowNegative={false}
        percentageSuffix
        fullWidth
        required
      />
      {isProviderIntervalCommissionsEnabled && (
        <CommissionIntervalForm name="payinConfig.intervalCommissions" />
      )}

      <Fragment>
        <Typography>
          {`${t('fields.trade_methods')} (${tCommon('common.payin')})`}
        </Typography>

        {!!multiplePaymentTypesError && (
          <Alert severity="error">{multiplePaymentTypesError}</Alert>
        )}

        <TradeMethodTreeSelect
          fieldName={'payinConfig.tradeMethods'}
          tradeMethods={availableTradeMethods}
          fiatCurrenciesIds={fiatCurrenciesIds}
        />
      </Fragment>

      <Box sx={{ mt: 2, mb: 3 }}>
        <PaymentTypeAmountLimits
          configPath="payinConfig"
          fiatCurrencyCode={getFiatCurrencyCode(fiatCurrencyId)}
        />
      </Box>

      <ConfigOverridesField
        fieldPath="payinConfig"
        providerType={providerType}
      />

      <Divider sx={{ mt: 2, mb: 2 }}>{t('fields.order_amount_limits')}</Divider>
      <FormControls row>
        <FormikNumericField
          name="payinConfig.orderMinAmount"
          label={tCommon('common.min')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
        <FormikNumericField
          name="payinConfig.orderMaxAmount"
          label={tCommon('common.max')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
      </FormControls>

      <FormikNumericField
        allowNegative={false}
        decimalScale={0}
        fullWidth
        label={t('fields.active_dispute_count')}
        name="payinConfig.activeDisputeCount"
      />

      <FormikNumericField
        allowNegative={false}
        decimalScale={0}
        fullWidth
        label={t('fields.daily_customer_completed_orders_limit')}
        name="payinConfig.dailyCustomerCompletedOrdersLimit"
        helperText={t(
          'fields.daily_customer_completed_orders_limit_description',
        )}
      />

      {isProviderLimitsEnabled && (
        <Fragment>
          <FormikNumericField
            allowNegative={false}
            decimalScale={0}
            fullWidth
            defaultValue={null}
            label={t('fields.limit_count_per_hour')}
            name="payinConfig.limitCountPerHour"
            helperText={t('fields.limit_count_per_hour_description')}
          />

          <FormikNumericField
            allowNegative={false}
            decimalScale={0}
            fullWidth
            defaultValue={null}
            label={t('fields.limit_count_per_day')}
            name="payinConfig.limitCountPerDay"
            helperText={t('fields.limit_count_per_day_description')}
          />
        </Fragment>
      )}

      <FormikCheckbox
        label={t('fields.allow_customer_bank')}
        name="payinConfig.allowCustomerBank"
      />

      <FormikCheckbox
        label={t('fields.allow_provider_amount_randomization')}
        name="payinConfig.allowProviderAmountRandomization"
      />

      <FormikCheckbox
        label={t('fields.use_provider_payment_url')}
        name="payinConfig.useProviderPaymentUrl"
        tooltip={t('fields.use_provider_payment_url_description')}
      />

      <FormikCheckbox
        label={t('fields.keep_nspk_phone_requisites')}
        name="payinConfig.keepNspkPhoneRequisites"
        tooltip={t('fields.keep_nspk_phone_requisites_description')}
      />

      {isProviderWorkingHoursEnabled && (
        <P2PProviderWorkingHours path="payinConfig" />
      )}

      <FormikCheckbox
        label={t('fields.require_customer_receipt')}
        name="payinConfig.collectCustomerReceipts"
        tooltip={t('fields.require_customer_receipt_description')}
      />
    </Fragment>
  );
};
